import React, { useState, useRef } from "react";
import { Modal } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import { slice1Actions } from "../../store/reducer";
import { DEFAULT_PACKING_CATEGORIES, itemId } from "../../Data/PackingList";
import "./PackingListDialog.css";

export default function PackingListDialog({ show, onHide }) {
  const dispatch = useDispatch();
  const GuideSettings = useSelector((s) => s.GuideSettings);

  const packing = GuideSettings?.packing_list ?? { checked: [], done: [], custom: [] };
  const neededSet = new Set(packing.checked ?? []);
  const doneSet = new Set(packing.done ?? []);
  const customItems = packing.custom ?? [];

  const [addingCategory, setAddingCategory] = useState(null);
  const [addText, setAddText] = useState("");
  const addInputRef = useRef(null);

  const save = (newNeeded, newDone, newCustom) => {
    const gs = JSON.parse(JSON.stringify(GuideSettings));
    gs.packing_list = {
      checked: Array.from(newNeeded),
      done: Array.from(newDone ?? doneSet),
      custom: newCustom ?? customItems,
    };
    dispatch(slice1Actions.setGuideSettings({ value: gs }));
  };

  const toggleNeeded = (id) => {
    const next = new Set(neededSet);
    if (next.has(id)) { next.delete(id); const d = new Set(doneSet); d.delete(id); save(next, d); }
    else { next.add(id); save(next); }
  };

  const toggleDone = (id) => {
    const next = new Set(doneSet);
    if (next.has(id)) next.delete(id);
    else { next.add(id); const n = new Set(neededSet); n.add(id); save(n, next); return; }
    save(neededSet, next);
  };

  const clearAll = () => save(new Set(), new Set(), customItems);

  const checkAll = () => {
    const all = new Set();
    DEFAULT_PACKING_CATEGORIES.forEach(({ category, items }) =>
      items.forEach((item) => all.add(itemId(category, item)))
    );
    customItems.forEach((c) => all.add(c.id));
    save(all);
  };

  const startAdd = (category) => {
    setAddingCategory(category);
    setAddText("");
    setTimeout(() => addInputRef.current?.focus(), 50);
  };

  const confirmAdd = () => {
    const label = addText.trim();
    if (!label || !addingCategory) { setAddingCategory(null); return; }
    const id = `${addingCategory}/${label}__custom_${Date.now()}`;
    const newCustom = [...customItems, { id, category: addingCategory, label }];
    save(neededSet, doneSet, newCustom);
    setAddingCategory(null);
    setAddText("");
  };

  const removeCustom = (id) => {
    const newCustom = customItems.filter((c) => c.id !== id);
    const n = new Set(neededSet); n.delete(id);
    const d = new Set(doneSet); d.delete(id);
    save(n, d, newCustom);
  };

  const doneCount = doneSet.size;
  const neededCount = neededSet.size;

  return (
    <Modal show={show} onHide={onHide} scrollable size="lg" className="pld-modal">
      <Modal.Header closeButton>
        <Modal.Title>
          Packing List
          <span className="pld-progress">
            {doneCount}/{neededCount} packed
          </span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="pld-body">
        {DEFAULT_PACKING_CATEGORIES.map(({ category, items }) => {
          const catCustom = customItems.filter((c) => c.category === category);
          const allCatItems = [
            ...items.map((label) => ({ id: itemId(category, label), label, isCustom: false })),
            ...catCustom.map((c) => ({ id: c.id, label: c.label, isCustom: true })),
          ];
          const catNeeded = allCatItems.filter((i) => neededSet.has(i.id)).length;
          const catDone = allCatItems.filter((i) => doneSet.has(i.id)).length;

          return (
            <div key={category} className="pld-category">
              <div className="pld-cat-header">
                <span className="pld-cat-title">{category}</span>
                <span className="pld-cat-count">{catDone}/{catNeeded}</span>
              </div>
              {allCatItems.map(({ id, label, isCustom }) => {
                const needed = neededSet.has(id);
                const done = doneSet.has(id);
                return (
                  <div key={id} className="pld-item">
                    <label className="pld-check-label pld-check-needed" title="Needed">
                      <input
                        type="checkbox"
                        className="pld-checkbox"
                        checked={needed}
                        onChange={() => toggleNeeded(id)}
                      />
                    </label>
                    <label className="pld-check-label pld-check-done" title="Packed">
                      <input
                        type="checkbox"
                        className="pld-checkbox pld-checkbox-done"
                        checked={done}
                        onChange={() => toggleDone(id)}
                      />
                    </label>
                    <span className={done ? "pld-item-text pld-item-done" : needed ? "pld-item-text pld-item-needed" : "pld-item-text"}>
                      {label}
                    </span>
                    {isCustom && (
                      <button
                        className="pld-remove-btn"
                        onClick={() => removeCustom(id)}
                        title="Remove item"
                      >
                        ×
                      </button>
                    )}
                  </div>
                );
              })}
              {addingCategory === category ? (
                <div className="pld-add-row">
                  <input
                    ref={addInputRef}
                    className="pld-add-input"
                    value={addText}
                    onChange={(e) => setAddText(e.target.value)}
                    placeholder="Item name…"
                    onKeyDown={(e) => { if (e.key === "Enter") confirmAdd(); if (e.key === "Escape") setAddingCategory(null); }}
                  />
                  <button className="pld-add-confirm" onClick={confirmAdd}>Add</button>
                  <button className="pld-add-cancel" onClick={() => setAddingCategory(null)}>Cancel</button>
                </div>
              ) : (
                <button className="pld-add-link" onClick={() => startAdd(category)}>
                  + Add item
                </button>
              )}
            </div>
          );
        })}
      </Modal.Body>
      <Modal.Footer className="pld-footer">
        <button className="pld-btn pld-btn-secondary" onClick={clearAll}>Clear All</button>
        <button className="pld-btn pld-btn-secondary" onClick={checkAll}>Check All</button>
        <button className="pld-btn pld-btn-primary" onClick={onHide}>Done</button>
      </Modal.Footer>
    </Modal>
  );
}
